import type { HolidaySection } from '@/src/types/holidays'

/**
 * Turns the cancellation wording the holidays API returns into timeline bands.
 *
 * The copy is editorial, not structured, and arrives in three shapes:
 *   "Prior to 60 days to 45 days: 50% of the holiday amount is nonrefundable"
 *   "44 and 30 days of departure: 75% of the holiday cost"
 *   "Less than 29 days prior to departure 100% of the total amount will be deducted."
 *   "10 days prior to departure 100% of the total amount will be deducted."
 */
export interface RefundBand {
    /** Days before departure at the start of the band; `null` means "from booking". */
    fromDay: number | null
    /** Days before departure at the end of the band; `null` means "up to departure". */
    toDay: number | null
    /** Share of the holiday cost retained on cancellation, 0-100. */
    chargePercent: number
}

export interface RefundTimelineSource {
    /** Section the schedule was read from, so it renders in the block it belongs to. */
    sectionId: string
    /**
     * `cancellation` — the section's own items are the schedule, so the graphic replaces them.
     * `footnote` — the schedule only rides along in a footnote of an unrelated section, whose
     * own content must still render; the graphic is added below it.
     */
    origin: 'cancellation' | 'footnote'
    bands: RefundBand[]
}

/** A window with two day numbers: "Prior to 60 days to 45 days: 50%", "44 and 30 days ... 75%". */
const RANGE = /(\d{1,3})\s*(?:days?)?\s*(?:to|and|-|–)\s*(\d{1,3})\s*days?[^.%]*?(\d{1,3})\s*%/gi
/** An open end: "Less than 29 days prior to departure 100%", "Within 10 days ... 100%". */
const WITHIN = /(?:less than|within|under|inside)\s*(\d{1,3})\s*days?[^.%]*?(\d{1,3})\s*%/gi
/** The other open end: "Prior to 45 days or more: 30%". */
const BEYOND = /(?:prior to\s*)?(\d{1,3})\s*days?\s*(?:or more|and above|\+)[^.%]*?(\d{1,3})\s*%/gi
/**
 * The same near end without a qualifier: "10 days prior to departure 100%". Some packages word the
 * final window this way, and without it the track stopped at the last stated range and said nothing
 * about the days closest to departure — the window where the traveller loses the most.
 */
const NEAR = /(\d{1,3})\s*days?\s*prior\s+to\s+departure[^.%]*?(\d{1,3})\s*%/gi

const CANCELLATION_SECTION = /cancel/i
/** The boilerplate schedule, which rides on whichever section happens to be last in the document. */
const GENERIC_SCHEDULE = /refunds?\s+will\s+be\s+based\s+on\s+how\s+many\s+days/i

const inRange = (n: number) => n >= 0 && n <= 100

function collect(text: string): RefundBand[] {
    const bands: RefundBand[] = []

    // `exec` in a loop rather than `matchAll`: this project targets es5, where iterating a
    // RegExpStringIterator needs downlevelIteration.
    const eachMatch = (pattern: RegExp, onMatch: (groups: string[]) => void) => {
        pattern.lastIndex = 0
        let match: RegExpExecArray | null
        while ((match = pattern.exec(text)) !== null) onMatch(match.slice(1))
    }

    // Order matters: the open-ended forms run first so their day number is not also consumed as
    // one half of a range by the looser RANGE pattern.
    eachMatch(BEYOND, ([days, percent]) => {
        const charge = Number(percent)
        if (inRange(charge)) bands.push({ fromDay: null, toDay: Number(days), chargePercent: charge })
    })
    eachMatch(WITHIN, ([days, percent]) => {
        const charge = Number(percent)
        if (inRange(charge)) bands.push({ fromDay: Number(days), toDay: null, chargePercent: charge })
    })
    // "Less than 29 days prior to departure" matches WITHIN too; the duplicate is dropped by the
    // one-band-per-charge rule below.
    eachMatch(NEAR, ([days, percent]) => {
        const charge = Number(percent)
        if (inRange(charge)) bands.push({ fromDay: Number(days), toDay: null, chargePercent: charge })
    })
    eachMatch(RANGE, ([a, b, percent]) => {
        const charge = Number(percent)
        if (inRange(charge)) {
            bands.push({
                fromDay: Math.max(Number(a), Number(b)),
                toDay: Math.min(Number(a), Number(b)),
                chargePercent: charge
            })
        }
    })

    return bands
}

/** Furthest from departure first: cancel now and pay least, cancel later and pay more. */
const byDistance = (a: RefundBand, b: RefundBand) => (b.fromDay ?? Infinity) - (a.fromDay ?? Infinity)

/**
 * Sorted furthest-first, each band must start no later than the previous one ended. A source that
 * breaks this is describing two different schedules at once — some packages have the boilerplate
 * pasted into their own cancellation section — and cannot be drawn as one timeline.
 */
function isContiguous(bands: RefundBand[]): boolean {
    for (let i = 0; i < bands.length - 1; i++) {
        const endOfCurrent = bands[i].toDay ?? 0
        const startOfNext = bands[i + 1].fromDay ?? Infinity
        if (startOfNext > endOfCurrent) return false
    }
    return true
}

/** Bands from one source, or `null` when the text does not describe a single coherent schedule. */
export function parseRefundTimeline(lines: string[]): RefundBand[] | null {
    const bands = lines.flatMap(collect)
    if (bands.length < 2) return null

    // One band per charge percentage: the same threshold is often restated within a source, and
    // duplicated segments would misreport the schedule.
    const byCharge = new Map<number, RefundBand>()
    for (const band of bands) if (!byCharge.has(band.chargePercent)) byCharge.set(band.chargePercent, band)

    const ordered = Array.from(byCharge.values()).sort(byDistance)
    return isContiguous(ordered) ? ordered : null
}

const linesOf = (section: HolidaySection): string[] =>
    section.type === 'bulletList' ? [...section.items, ...(section.footnotes ?? [])] : []

/**
 * Picks the one schedule to draw.
 *
 * A package's own cancellation section wins outright. The generic boilerplate footnote is only
 * read when the package has no cancellation section at all — merging the two produced overlapping
 * bands that contradicted each other (one source charging 45% for the same window the other
 * charged 75%), and this is refund money, so the package's own terms are the only safe source.
 *
 * A cancellation section that states a flat rule rather than a schedule ("once confirmed the
 * amount is non refundable") yields no timeline: its own words stand, unreplaced by boilerplate.
 */
export function selectRefundTimeline(sections: HolidaySection[]): RefundTimelineSource | null {
    const cancellation = sections.find(section => CANCELLATION_SECTION.test(section.id))
    if (cancellation) {
        const bands = parseRefundTimeline(linesOf(cancellation))
        return bands ? { sectionId: cancellation.id, origin: 'cancellation', bands } : null
    }

    for (const section of sections) {
        if (section.type !== 'bulletList') continue
        const footnotes = (section.footnotes ?? []).filter(note => GENERIC_SCHEDULE.test(note))
        if (!footnotes.length) continue
        const bands = parseRefundTimeline(footnotes)
        if (bands) return { sectionId: section.id, origin: 'footnote', bands }
    }

    return null
}
